import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { resolveAssetUrl, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { useSite } from "@/context/SiteContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { LogOut, Plus, Trash2, Pencil, Mail, Briefcase, MessageSquare, HelpCircle, FileEdit, Loader2, ExternalLink, Phone, MapPin, Image as ImageIcon, Upload } from "lucide-react";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { content, refresh: refreshSite } = useSite();
  const [tab, setTab] = useState("leads");
  const [data, setData] = useState({ leads: [], visits: [], contacts: [], services: [], testimonials: [], faqs: [], site_content: [], partners: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!localStorage.getItem("dc_token")) {
      navigate("/admin/login");
      return;
    }
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [l, v, c, s, t, f, sc, p] = await Promise.all([
        api.get("/admin/leads"),
        api.get("/admin/visits"),
        api.get("/admin/contacts"),
        api.get("/admin/services"),
        api.get("/admin/testimonials"),
        api.get("/admin/faqs"),
        api.get("/admin/site-content"),
        api.get("/admin/partners"),
      ]);
      setData({ leads: l.data, visits: v.data, contacts: c.data, services: s.data, testimonials: t.data, faqs: f.data, site_content: sc.data, partners: p.data });
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("dc_token");
        navigate("/admin/login");
      } else {
        toast.error("Erro ao carregar dados");
      }
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("dc_token");
    navigate("/admin/login");
  };

  return (
    <div data-testid="page-admin-dashboard" className="min-h-screen bg-[#f5f7fa]">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="dc-container flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <img src="/brand/logo-light.png" alt="DeskcodeBR" className="h-9 w-auto" />
            <div className="border-l border-slate-200 pl-3">
              <div className="font-heading font-bold text-dc-text text-sm">Admin</div>
              <div className="text-xs text-slate-500">{localStorage.getItem("dc_email")}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" className="text-sm text-slate-600 hover:text-dc-primary inline-flex items-center gap-1.5"><ExternalLink className="w-4 h-4" /> Ver site</Link>
            <Button data-testid="admin-logout" variant="outline" onClick={logout} className="rounded-full"><LogOut className="w-4 h-4 mr-2" /> Sair</Button>
          </div>
        </div>
      </header>

      <div className="dc-container py-8">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-dc-text mb-6">Painel administrativo</h1>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-dc-primary" /></div>
        ) : (
          <Tabs value={tab} onValueChange={setTab} className="space-y-6">
            <TabsList className="bg-white border border-slate-200 p-1.5 rounded-full flex flex-wrap h-auto">
              <TabsTrigger value="leads" data-testid="tab-leads" className="rounded-full data-[state=active]:bg-dc-primary data-[state=active]:text-white"><Mail className="w-4 h-4 mr-2" />Orçamentos ({data.leads.length})</TabsTrigger>
              <TabsTrigger value="visits" data-testid="tab-visits" className="rounded-full data-[state=active]:bg-dc-primary data-[state=active]:text-white"><MapPin className="w-4 h-4 mr-2" />Visitas ({data.visits.length})</TabsTrigger>
              <TabsTrigger value="contacts" data-testid="tab-contacts" className="rounded-full data-[state=active]:bg-dc-primary data-[state=active]:text-white"><Phone className="w-4 h-4 mr-2" />Contatos ({data.contacts.length})</TabsTrigger>
              <TabsTrigger value="services" data-testid="tab-services" className="rounded-full data-[state=active]:bg-dc-primary data-[state=active]:text-white"><Briefcase className="w-4 h-4 mr-2" />Serviços</TabsTrigger>
              <TabsTrigger value="testimonials" data-testid="tab-testimonials" className="rounded-full data-[state=active]:bg-dc-primary data-[state=active]:text-white"><MessageSquare className="w-4 h-4 mr-2" />Depoimentos</TabsTrigger>
              <TabsTrigger value="faqs" data-testid="tab-faqs" className="rounded-full data-[state=active]:bg-dc-primary data-[state=active]:text-white"><HelpCircle className="w-4 h-4 mr-2" />FAQ</TabsTrigger>
              <TabsTrigger value="partners" data-testid="tab-partners" className="rounded-full data-[state=active]:bg-dc-primary data-[state=active]:text-white"><ImageIcon className="w-4 h-4 mr-2" />Parceiros</TabsTrigger>
              <TabsTrigger value="content" data-testid="tab-content" className="rounded-full data-[state=active]:bg-dc-primary data-[state=active]:text-white"><FileEdit className="w-4 h-4 mr-2" />Conteúdo</TabsTrigger>
              <TabsTrigger value="brand" data-testid="tab-brand" className="rounded-full data-[state=active]:bg-dc-primary data-[state=active]:text-white"><ImageIcon className="w-4 h-4 mr-2" />Marca</TabsTrigger>
            </TabsList>

            <TabsContent value="leads"><LeadsTable items={data.leads} reload={loadAll} /></TabsContent>
			<TabsContent value="visits"><VisitsTable items={data.visits} reload={loadAll} /></TabsContent>
            <TabsContent value="contacts"><ContactsTable items={data.contacts} reload={loadAll} /></TabsContent>
            <TabsContent value="services"><ServicesAdmin items={data.services} reload={loadAll} /></TabsContent>
            <TabsContent value="testimonials"><TestimonialsAdmin items={data.testimonials} reload={loadAll} /></TabsContent>
            <TabsContent value="faqs"><FAQsAdmin items={data.faqs} reload={loadAll} /></TabsContent>
            <TabsContent value="partners"><PartnersAdmin items={data.partners} reload={loadAll} /></TabsContent>
            <TabsContent value="content"><ContentAdmin items={data.site_content} reload={loadAll} /></TabsContent>
            <TabsContent value="brand"><BrandAdmin reload={() => { loadAll(); refreshSite(); }} content={content} /></TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}

const Card = ({ children, className = "" }) => (<div className={`bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden ${className}`}>{children}</div>);
const Empty = ({ msg }) => (<Card className="p-10 text-center text-slate-500">{msg}</Card>);

const LeadsTable = ({ items, reload }) => {
  const complete = async (id) => {
    if (!window.confirm("Marcar este orçamento como concluído?")) return;
    try {
      await api.put(`/admin/leads/${id}/complete`);
      toast.success("Orçamento concluído");
      reload?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao concluir orçamento");
    }
  };

  if (!items.length) return <Empty msg="Nenhum orçamento pendente." />;
  return (
    <div className="grid gap-4">
      {items.map((l) => (
        <Card key={l.id} className="p-6">
          <div className="flex flex-wrap justify-between gap-3 mb-3">
            <div>
              <div className="font-heading font-semibold text-dc-text">{l.name} {l.company && <span className="text-slate-400 text-sm">• {l.company}</span>}</div>
              <div className="text-xs text-slate-500 mt-1">{new Date(l.created_at).toLocaleString("pt-BR")}</div>
            </div>
            <div className="flex gap-2 text-sm">
              <a href={`mailto:${l.email}`} className="text-dc-primary hover:underline">{l.email}</a>
              <span className="text-slate-300">•</span>
              <a href={`https://wa.me/${l.phone.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="text-dc-primary hover:underline">{l.phone}</a>
            </div>
          </div>
          {l.service && <div className="text-sm text-slate-600 mb-2"><b>Serviço:</b> {l.service}</div>}
          {l.city && <div className="text-sm text-slate-600 mb-2"><b>Cidade:</b> {l.city}</div>}
          {l.best_time && <div className="text-sm text-slate-600 mb-2"><b>Horário preferido:</b> {l.best_time}</div>}
          <div className="text-sm text-slate-700 bg-slate-50 rounded-lg p-3 mt-2">{l.description}</div>
          <div className="flex flex-wrap justify-end gap-2 mt-4">
            <Button
              type="button"
              onClick={() => complete(l.id)}
              className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              Concluir orçamento
            </Button>
          </div>
        </Card>
      ))}
    </div>
  );
};

const VisitsTable = ({ items, reload }) => {
  const complete = async (id) => {
    if (!window.confirm("Marcar esta visita como concluída?")) return;
    try {
      await api.put(`/admin/visits/${id}/complete`);
      toast.success("Visita concluída");
      reload?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao concluir visita");
    }
  };

  if (!items.length) return <Empty msg="Nenhuma visita pendente." />;
  return (
    <div className="grid gap-4">
      {items.map((v) => (
        <Card key={v.id} className="p-6">
          <div className="flex flex-wrap justify-between gap-3 mb-3">
            <div>
              <div className="font-heading font-semibold text-dc-text">{v.name} {v.company && <span className="text-slate-400 text-sm">• {v.company}</span>}</div>
              <div className="text-xs text-slate-500 mt-1">{new Date(v.created_at).toLocaleString("pt-BR")}</div>
            </div>
            <span className={`text-xs px-3 py-1 rounded-full font-semibold ${v.urgency === "critica" ? "bg-red-100 text-red-700" : v.urgency === "alta" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>{v.urgency}</span>
          </div>
          <div className="grid md:grid-cols-2 gap-3 text-sm text-slate-600">
            <div><b>Endereço:</b> {v.address}, {v.city}</div>
            <div><b>Contato:</b> {v.phone} • {v.email}</div>
            <div><b>Problema:</b> {v.problem_type}</div>
            <div><b>Equipamentos:</b> {v.equipment || "—"}</div>
            <div><b>Data preferida:</b> {v.preferred_date || "—"} {v.preferred_time}</div>
          </div>
          {v.notes && <div className="text-sm text-slate-700 bg-slate-50 rounded-lg p-3 mt-3">{v.notes}</div>}
          <div className="flex flex-wrap justify-end gap-2 mt-4">
            <Button
              type="button"
              onClick={() => complete(v.id)}
              className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              Concluir visita
            </Button>
          </div>
        </Card>
      ))}
    </div>
  );
};

const ContactsTable = ({ items, reload }) => {
  const complete = async (id) => {
    if (!window.confirm("Marcar este contato como concluído?")) return;
    try {
      await api.put(`/admin/contacts/${id}/complete`);
      toast.success("Contato concluído");
      reload?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao concluir contato");
    }
  };

  if (!items.length) return <Empty msg="Nenhum contato pendente." />;
  return (
    <div className="grid gap-4">
      {items.map((c) => (
        <Card key={c.id} className="p-6">
          <div className="flex flex-wrap justify-between gap-3 mb-3">
            <div>
              <div className="font-heading font-semibold text-dc-text">{c.name}</div>
              <div className="text-xs text-slate-500 mt-1">{new Date(c.created_at).toLocaleString("pt-BR")}</div>
            </div>
            <div className="flex gap-2 text-sm">
              <a href={`mailto:${c.email}`} className="text-dc-primary hover:underline">{c.email}</a>
              {c.phone && <><span className="text-slate-300">•</span><a href={`https://wa.me/${c.phone.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="text-dc-primary hover:underline">{c.phone}</a></>}
            </div>
          </div>
          {c.subject && <div className="text-sm text-slate-600 mb-2"><b>Assunto:</b> {c.subject}</div>}
          <div className="text-sm text-slate-700 bg-slate-50 rounded-lg p-3 mt-2">{c.message}</div>
          <div className="flex flex-wrap justify-end gap-2 mt-4">
            <Button
              type="button"
              onClick={() => complete(c.id)}
              className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              Concluir contato
            </Button>
          </div>
        </Card>
      ))}
    </div>
  );
};

// Generic CRUD form for services/testimonials/faqs
const ServiceForm = ({ initial, onSave, onClose }) => {
  const [form, setForm] = useState(initial || { title: "", short_description: "", full_description: "", icon: "Wrench", order: 0, active: true, estimated_price: 0, price_unit: "a partir de" });
  return (
    <div className="space-y-4">
      <div><Label>Título</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
      <div><Label>Descrição curta</Label><Textarea value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} rows={3} /></div>
      <div><Label>Descrição completa</Label><Textarea value={form.full_description} onChange={(e) => setForm({ ...form, full_description: e.target.value })} rows={4} /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Ícone (lucide-react)</Label><Input value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} placeholder="Ex: Server, Cloud, Code2" /></div>
        <div><Label>Ordem</Label><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value || 0) })} /></div>
      </div>
      <div className="grid grid-cols-2 gap-3 border-t border-slate-100 pt-3">
        <div><Label>Preço estimado (R$) <span className="text-xs text-slate-400">— para calculadora</span></Label><Input type="number" step="50" value={form.estimated_price ?? 0} onChange={(e) => setForm({ ...form, estimated_price: parseFloat(e.target.value || 0) })} placeholder="0 = oculta da calculadora" /></div>
        <div><Label>Prefixo de preço</Label><Input value={form.price_unit ?? "a partir de"} onChange={(e) => setForm({ ...form, price_unit: e.target.value })} placeholder="a partir de" /></div>
      </div>
      <div className="flex items-center gap-3"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /><Label>Ativo</Label></div>
      <div className="flex justify-end gap-2"><Button variant="outline" onClick={onClose}>Cancelar</Button><Button onClick={() => onSave(form)} className="bg-dc-primary text-white">Salvar</Button></div>
    </div>
  );
};

const ServicesAdmin = ({ items, reload }) => {
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const save = async (form) => {
    try {
      if (editing?.id) await api.put(`/admin/services/${editing.id}`, form);
      else await api.post("/admin/services", form);
      toast.success("Serviço salvo");
      setOpen(false); setEditing(null); reload();
    } catch { toast.error("Erro ao salvar"); }
  };
  const del = async (id) => { if (!window.confirm("Excluir?")) return; await api.delete(`/admin/services/${id}`); toast.success("Excluído"); reload(); };
  return (
    <div>
      <div className="flex justify-end mb-4">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-service" onClick={() => setEditing(null)} className="bg-dc-primary text-white rounded-full"><Plus className="w-4 h-4 mr-2" /> Novo serviço</Button></DialogTrigger>
          <DialogContent className="max-w-xl"><DialogHeader><DialogTitle>{editing ? "Editar" : "Novo"} serviço</DialogTitle></DialogHeader><ServiceForm initial={editing} onSave={save} onClose={() => setOpen(false)} /></DialogContent>
        </Dialog>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {items.map((s) => (
          <Card key={s.id} className="p-5">
            <div className="flex justify-between gap-3 mb-2"><div className="font-heading font-semibold text-dc-text">{s.title}</div><div className="flex gap-1"><Button size="icon" variant="ghost" onClick={() => { setEditing(s); setOpen(true); }}><Pencil className="w-4 h-4" /></Button><Button size="icon" variant="ghost" onClick={() => del(s.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button></div></div>
            <div className="text-sm text-slate-600">{s.short_description}</div>
            <div className="text-xs text-slate-400 mt-2">Ícone: {s.icon} • Ordem: {s.order} • {s.active ? "Ativo" : "Inativo"}</div>
          </Card>
        ))}
      </div>
    </div>
  );
};

const TestimonialForm = ({ initial, onSave, onClose }) => {
  const [form, setForm] = useState(initial || { name: "", role: "", company: "", content: "", rating: 5, order: 0, active: true });
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3"><div><Label>Nome</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div><div><Label>Cargo</Label><Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} /></div></div>
      <div><Label>Empresa</Label><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></div>
      <div><Label>Depoimento</Label><Textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={4} /></div>
      <div className="grid grid-cols-2 gap-3"><div><Label>Nota (1-5)</Label><Input type="number" min="1" max="5" value={form.rating} onChange={(e) => setForm({ ...form, rating: parseInt(e.target.value || 5) })} /></div><div><Label>Ordem</Label><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value || 0) })} /></div></div>
      <div className="flex items-center gap-3"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /><Label>Ativo</Label></div>
      <div className="flex justify-end gap-2"><Button variant="outline" onClick={onClose}>Cancelar</Button><Button onClick={() => onSave(form)} className="bg-dc-primary text-white">Salvar</Button></div>
    </div>
  );
};

const TestimonialsAdmin = ({ items, reload }) => {
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const save = async (form) => {
    try {
      if (editing?.id) await api.put(`/admin/testimonials/${editing.id}`, form);
      else await api.post("/admin/testimonials", form);
      toast.success("Depoimento salvo"); setOpen(false); setEditing(null); reload();
    } catch { toast.error("Erro"); }
  };
  const del = async (id) => { if (!window.confirm("Excluir?")) return; await api.delete(`/admin/testimonials/${id}`); reload(); };
  return (
    <div>
      <div className="flex justify-end mb-4">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button onClick={() => setEditing(null)} className="bg-dc-primary text-white rounded-full"><Plus className="w-4 h-4 mr-2" /> Novo depoimento</Button></DialogTrigger>
          <DialogContent className="max-w-xl"><DialogHeader><DialogTitle>{editing ? "Editar" : "Novo"} depoimento</DialogTitle></DialogHeader><TestimonialForm initial={editing} onSave={save} onClose={() => setOpen(false)} /></DialogContent>
        </Dialog>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {items.map((t) => (
          <Card key={t.id} className="p-5">
            <div className="flex justify-between mb-2"><div className="font-heading font-semibold text-dc-text">{t.name} <span className="text-slate-400 text-sm">• {t.role}</span></div><div className="flex gap-1"><Button size="icon" variant="ghost" onClick={() => { setEditing(t); setOpen(true); }}><Pencil className="w-4 h-4" /></Button><Button size="icon" variant="ghost" onClick={() => del(t.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button></div></div>
            <div className="text-sm text-slate-600 italic">"{t.content}"</div>
          </Card>
        ))}
      </div>
    </div>
  );
};

const FAQForm = ({ initial, onSave, onClose }) => {
  const [form, setForm] = useState(initial || { question: "", answer: "", order: 0, active: true });
  return (
    <div className="space-y-4">
      <div><Label>Pergunta</Label><Input value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} /></div>
      <div><Label>Resposta</Label><Textarea value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} rows={4} /></div>
      <div><Label>Ordem</Label><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value || 0) })} /></div>
      <div className="flex items-center gap-3"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /><Label>Ativo</Label></div>
      <div className="flex justify-end gap-2"><Button variant="outline" onClick={onClose}>Cancelar</Button><Button onClick={() => onSave(form)} className="bg-dc-primary text-white">Salvar</Button></div>
    </div>
  );
};

const FAQsAdmin = ({ items, reload }) => {
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const save = async (form) => {
    try {
      if (editing?.id) await api.put(`/admin/faqs/${editing.id}`, form);
      else await api.post("/admin/faqs", form);
      toast.success("FAQ salva"); setOpen(false); setEditing(null); reload();
    } catch { toast.error("Erro"); }
  };
  const del = async (id) => { if (!window.confirm("Excluir?")) return; await api.delete(`/admin/faqs/${id}`); reload(); };
  return (
    <div>
      <div className="flex justify-end mb-4">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button onClick={() => setEditing(null)} className="bg-dc-primary text-white rounded-full"><Plus className="w-4 h-4 mr-2" /> Nova pergunta</Button></DialogTrigger>
          <DialogContent className="max-w-xl"><DialogHeader><DialogTitle>{editing ? "Editar" : "Nova"} pergunta</DialogTitle></DialogHeader><FAQForm initial={editing} onSave={save} onClose={() => setOpen(false)} /></DialogContent>
        </Dialog>
      </div>
      <div className="grid gap-3">
        {items.map((f) => (
          <Card key={f.id} className="p-5">
            <div className="flex justify-between gap-3"><div><div className="font-heading font-semibold text-dc-text">{f.question}</div><div className="text-sm text-slate-600 mt-1">{f.answer}</div></div><div className="flex gap-1"><Button size="icon" variant="ghost" onClick={() => { setEditing(f); setOpen(true); }}><Pencil className="w-4 h-4" /></Button><Button size="icon" variant="ghost" onClick={() => del(f.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button></div></div>
          </Card>
        ))}
      </div>
    </div>
  );
};

const ContentAdmin = ({ items, reload }) => {
  const visibleItems = items.filter((i) => !["logo_url_light", "logo_url_dark", "logo_url_white"].includes(i.key));
  const [edited, setEdited] = useState({});
  const [uploadingKey, setUploadingKey] = useState(null);

  useEffect(() => {
    const init = {};
    visibleItems.forEach((i) => { init[i.key] = i.value; });
    setEdited(init);
  }, [items]);

  const save = async () => {
    try {
      const payload = visibleItems.map((i) => ({ key: i.key, value: edited[i.key] ?? i.value }));
      await api.put("/admin/site-content", payload);
      toast.success("Conteúdo atualizado"); reload();
    } catch { toast.error("Erro ao salvar"); }
  };

  const uploadContentImage = async (key, file) => {
    if (!file) return;
    setUploadingKey(key);
    try {
      const formData = new FormData();
      formData.append("key", key);
      formData.append("file", file);
      const { data } = await api.post("/admin/upload-content-image", formData, { headers: { "Content-Type": "multipart/form-data" } });
      setEdited((e) => ({ ...e, [key]: data.url }));
      toast.success("Imagem enviada");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro no upload da imagem");
    } finally {
      setUploadingKey(null);
    }
  };

  const imageKeys = ["about_image_url", "home_hero_image", "sobre_hero_image", "servicos_hero_image"];
  const isImageKey = (key) => imageKeys.includes(key);
  const isLong = (key) => ["text", "about", "mission", "vision", "values", "subtitle", "map", "embed"].some((k) => key.includes(k));

  return (
    <Card className="p-6">
      <div className="grid md:grid-cols-2 gap-4">
        {visibleItems.map((i) => (
          <div key={i.key} className={isLong(i.key) || isImageKey(i.key) ? "md:col-span-2" : ""}>
            <Label className="text-xs uppercase tracking-wider text-slate-500">{i.key}</Label>

            {isImageKey(i.key) ? (
              <div className="space-y-3">
                {(edited[i.key] || i.value) && (
                  <div className="bg-[#f5f7fa] border border-slate-100 rounded-xl p-3 flex items-center justify-center min-h-[140px]">
                    <img
                      src={resolveAssetUrl(edited[i.key] ?? i.value, edited[i.key] ?? i.value)}
                      alt={i.key}
                      className="max-h-56 max-w-full object-contain rounded-lg"
                    />
                  </div>
                )}
                <div className="flex flex-col md:flex-row gap-2">
                  <Input value={edited[i.key] ?? ""} onChange={(e) => setEdited({ ...edited, [i.key]: e.target.value })} placeholder="URL da imagem ou faça upload" />
                  <label className="shrink-0">
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      className="hidden"
                      onChange={(e) => uploadContentImage(i.key, e.target.files?.[0])}
                    />
                    <span className={`inline-flex items-center justify-center gap-2 bg-dc-primary hover:bg-dc-primary-hover text-white px-5 h-10 rounded-full font-semibold cursor-pointer transition ${uploadingKey === i.key ? "opacity-60 cursor-wait" : ""}`}>
                      {uploadingKey === i.key ? <><Loader2 className="w-4 h-4 animate-spin" /> Enviando...</> : <><Upload className="w-4 h-4" /> Upload</>}
                    </span>
                  </label>
                </div>
              </div>
            ) : isLong(i.key) ? (
              <Textarea value={edited[i.key] ?? ""} onChange={(e) => setEdited({ ...edited, [i.key]: e.target.value })} rows={i.key.includes("map") || i.key.includes("embed") ? 5 : 3} />
            ) : (
              <Input value={edited[i.key] ?? ""} onChange={(e) => setEdited({ ...edited, [i.key]: e.target.value })} />
            )}
          </div>
        ))}
      </div>
      <div className="flex justify-end mt-6"><Button data-testid="save-content" onClick={save} className="bg-dc-primary text-white rounded-full">Salvar alterações</Button></div>
    </Card>
  );
};

const BrandAdmin = ({ reload, content }) => {
  const [uploading, setUploading] = useState({ light: false, dark: false, white: false });

  const upload = async (kind, file) => {
    if (!file) return;
    setUploading((u) => ({ ...u, [kind]: true }));
    try {
      const formData = new FormData();
      formData.append("kind", kind);
      formData.append("file", file);
      await api.post("/admin/upload-logo", formData, { headers: { "Content-Type": "multipart/form-data" } });
      const labels = { light: "claro", dark: "escuro", white: "branco" };
      toast.success(`Logo ${labels[kind]} atualizado!`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro no upload");
    } finally {
      setUploading((u) => ({ ...u, [kind]: false }));
    }
  };

  const lightUrl = resolveAssetUrl(content?.logo_url_light, "/brand/logo-light.png");
  const darkUrl = resolveAssetUrl(content?.logo_url_dark, "/brand/logo-dark.png");
  const whiteUrl = resolveAssetUrl(content?.logo_url_white, "/brand/logo-white.png");

  const Slot = ({ kind, title, desc, url, bg }) => (
    <Card className="p-7">
      <h3 className="font-heading text-lg font-bold text-dc-text mb-1">{title}</h3>
      <p className="text-sm text-slate-500 mb-5">{desc}</p>
      <div className={`rounded-2xl p-8 flex items-center justify-center min-h-[160px] mb-5 ${bg}`}>
        <img src={url} alt={title} className="max-h-32 max-w-full object-contain" />
      </div>
      <label className="block">
        <input
          data-testid={`upload-logo-${kind}`}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/svg+xml"
          className="hidden"
          onChange={(e) => upload(kind, e.target.files?.[0])}
        />
        <span className={`inline-flex items-center justify-center gap-2 w-full bg-dc-primary hover:bg-dc-primary-hover text-white py-3 rounded-full font-semibold cursor-pointer transition ${uploading[kind] ? "opacity-60 cursor-wait" : ""}`}>
          {uploading[kind] ? <><Loader2 className="w-4 h-4 animate-spin" /> Enviando...</> : <><Upload className="w-4 h-4" /> Substituir</>}
        </span>
      </label>
    </Card>
  );

  return (
    <div className="grid md:grid-cols-3 gap-6">
      <Slot kind="light" title="Logo colorido (header scrolled)" desc="Usado quando o usuário rolar a página (fundo branco). PNG transparente recomendado. Máx 5MB." url={lightUrl} bg="bg-[#f5f7fa] border border-slate-100" />
      <Slot kind="white" title="Logo branco (hero)" desc="Usado no topo do site sobre o fundo escuro do hero. PNG com texto branco e fundo transparente. Máx 5MB." url={whiteUrl} bg="bg-gradient-to-br from-dc-secondary to-dc-primary" />
      <Slot kind="dark" title="Logo do rodapé" desc="Usado no footer (fundo escuro). PNG transparente recomendado. Máx 5MB." url={darkUrl} bg="bg-[#0a1128]" />
    </div>
  );
};

const PartnerForm = ({ initial, onSave, onClose }) => {
  const [form, setForm] = useState(initial || { name: "", logo_url: "", order: 0, active: true });
  const [uploading, setUploading] = useState(false);
  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload-partner-logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, logo_url: data.url }));
      toast.success("Logo do parceiro enviado");
    } catch (e) {
      toast.error("Erro no upload");
    } finally {
      setUploading(false);
    }
  };
  return (
    <div className="space-y-4">
      <div><Label>Nome do parceiro</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
      <div>
        <Label>Logo</Label>
        {form.logo_url && <div className="bg-[#f5f7fa] rounded-lg p-4 flex items-center justify-center mb-2 border border-slate-100"><img src={resolveAssetUrl(form.logo_url, form.logo_url)} alt={form.name} className="max-h-16" /></div>}
        <div className="flex gap-2">
          <Input value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="URL ou faça upload" />
          <label>
            <input type="file" accept="image/*" className="hidden" onChange={(e) => handleUpload(e.target.files?.[0])} />
            <span className={`inline-flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 px-4 h-10 rounded-md text-sm cursor-pointer ${uploading ? "opacity-60" : ""}`}>
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Upload
            </span>
          </label>
        </div>
      </div>
      <div><Label>Ordem</Label><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value || 0) })} /></div>
      <div className="flex items-center gap-3"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /><Label>Ativo</Label></div>
      <div className="flex justify-end gap-2"><Button variant="outline" onClick={onClose}>Cancelar</Button><Button onClick={() => onSave(form)} className="bg-dc-primary text-white">Salvar</Button></div>
    </div>
  );
};

const PartnersAdmin = ({ items, reload }) => {
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const save = async (form) => {
    try {
      if (editing?.id) await api.put(`/admin/partners/${editing.id}`, form);
      else await api.post("/admin/partners", form);
      toast.success("Parceiro salvo"); setOpen(false); setEditing(null); reload();
    } catch { toast.error("Erro ao salvar"); }
  };
  const del = async (id) => { if (!window.confirm("Excluir parceiro?")) return; await api.delete(`/admin/partners/${id}`); reload(); };
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-600">Para esconder a seção inteira de parceiros, edite a chave <code className="bg-slate-100 px-1.5 py-0.5 rounded">partners_visible</code> em <b>Conteúdo</b> e troque para <code>false</code>.</p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-partner" onClick={() => setEditing(null)} className="bg-dc-primary text-white rounded-full"><Plus className="w-4 h-4 mr-2" /> Novo parceiro</Button></DialogTrigger>
          <DialogContent className="max-w-xl"><DialogHeader><DialogTitle>{editing ? "Editar" : "Novo"} parceiro</DialogTitle></DialogHeader><PartnerForm initial={editing} onSave={save} onClose={() => setOpen(false)} /></DialogContent>
        </Dialog>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((p) => (
          <Card key={p.id} className="p-5">
            <div className="bg-[#f5f7fa] rounded-lg h-20 flex items-center justify-center mb-3 border border-slate-100">
              <img src={resolveAssetUrl(p.logo_url, p.logo_url)} alt={p.name} className="max-h-12 max-w-[80%] object-contain" />
            </div>
            <div className="flex justify-between gap-2"><div className="flex-1"><div className="font-heading font-semibold text-dc-text text-sm">{p.name}</div><div className="text-xs text-slate-400">Ordem {p.order} • {p.active ? "Ativo" : "Inativo"}</div></div><div className="flex gap-1"><Button size="icon" variant="ghost" onClick={() => { setEditing(p); setOpen(true); }}><Pencil className="w-4 h-4" /></Button><Button size="icon" variant="ghost" onClick={() => del(p.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button></div></div>
          </Card>
        ))}
        {!items.length && <p className="col-span-full text-center text-slate-500 py-10">Nenhum parceiro cadastrado.</p>}
      </div>
    </div>
  );
};
